/**
 * useStepUp — shared step-up challenge flow (BRD NFR-SEC-05).
 *
 * Sensitive endpoints (medical-alerts reveal, cashier-scope edit, bank-account
 * edit, integration credentials, etc.) expect a short-lived token in a request
 * header. The aspect-based path validates {@code X-Step-Up-Token}; a couple
 * of legacy bank/integration routes still read {@code X-Kori-Step-Up}. This
 * composable centralises the two knobs callers need (action name + header
 * name) and funnels all flows through a single modal instance so we don't
 * copy-paste the header wiring a third time.
 *
 * Usage — direct token:
 *   const { requireStepUp } = useStepUp()
 *   const token = await requireStepUp('student.medical.reveal')
 *   await revealMedicalAlerts(id, token)
 *
 * Usage — automatic retry-on-403:
 *   const { withStepUp } = useStepUp()
 *   await withStepUp(
 *     (headers) => apiClient.put(url, body, { headers }),
 *     { action: 'staff.cashier_scope.edit' },
 *   )
 *
 * The {@code withStepUp} helper runs the caller once; if the server answers
 * with the {@code stepup_required} challenge body ({@code 403 +
 * code: 'stepup_required'}), it pops the modal, collects a token, and retries
 * once with the header injected. Other errors propagate untouched.
 *
 * Token issuance — the Platform API does not yet expose a challenge/verify
 * route (see {@code StaffController} TODO: "Wire to the full MFA / WebAuthn
 * challenge once StepUpAuthService is fully online"). Until it does, the
 * default issuer POSTs to {@code /v1/auth/step-up/issue} with the action +
 * the MFA code the user typed; tests and the future real issuance flow can
 * swap this via {@link configureStepUpIssuer}. No server-side change is made
 * here; the endpoint name is just what the modal will call.
 */
import { ref, shallowRef, readonly } from 'vue'
import { apiClient, type ApiError } from '@/api/client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StepUpHeaderName = 'X-Step-Up-Token' | 'X-Kori-Step-Up'

export interface StepUpRequest {
  /** Action key the server binds to, e.g. {@code 'student.medical.reveal'}. */
  action: string
  /** Defaults to {@code 'X-Step-Up-Token'}. Use {@code 'X-Kori-Step-Up'} for
   * the bank-account / integration routes that read the legacy header. */
  header?: StepUpHeaderName
  /** Optional human-readable label surfaced in the modal body so users know
   * which operation they're approving. */
  label?: string
}

export interface StepUpChallenge extends StepUpRequest {
  id: string
  resolve: (token: string) => void
  reject: (reason: unknown) => void
}

/** Shape returned by the (future) issuance endpoint. */
export interface StepUpIssueResponse {
  token: string
  expiresAt?: string
}

export type StepUpIssuer = (input: { action: string; code: string }) => Promise<StepUpIssueResponse>

// ---------------------------------------------------------------------------
// Singleton state — the modal is app-wide so concurrent callers queue up.
// ---------------------------------------------------------------------------

const activeChallenge = shallowRef<StepUpChallenge | null>(null)
const queue: StepUpChallenge[] = []
const lastError = ref<string | null>(null)

let issuer: StepUpIssuer = async ({ action, code }) => {
  const { data } = await apiClient.post<StepUpIssueResponse>('/v1/auth/step-up/issue', {
    action,
    code,
  })
  return data
}

/** Swap the issuer (tests, or wiring to the real MFA/WebAuthn flow later). */
export function configureStepUpIssuer(next: StepUpIssuer): void {
  issuer = next
}

function nextChallenge(): void {
  activeChallenge.value = queue.shift() ?? null
  lastError.value = null
}

function openChallenge(req: StepUpRequest): Promise<string> {
  return new Promise((resolve, reject) => {
    const entry: StepUpChallenge = {
      id: (globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random())),
      action: req.action,
      header: req.header ?? 'X-Step-Up-Token',
      label: req.label,
      resolve,
      reject,
    }
    if (activeChallenge.value) queue.push(entry)
    else activeChallenge.value = entry
  })
}

// ---------------------------------------------------------------------------
// Error detection — both the aspect path and the legacy bank/integration
// path respond with 403. The aspect path includes `code: 'stepup_required'`;
// the legacy paths currently return a plain 403 without that body, so we
// also treat a 403 as challenge-worthy when the caller opted in via the
// `detectLegacy` flag. See StepUpExceptionHandler.java for the canonical body.
// ---------------------------------------------------------------------------

function isStepUpRequired(err: unknown, detectLegacy = false): boolean {
  const ae = err as Partial<ApiError> | null | undefined
  if (ae?.status !== 403) return false
  if (ae.code === 'stepup_required') return true
  // Legacy bank/integration endpoints don't wrap with the aspect handler yet.
  // Callers that target those routes pass detectLegacy=true so a bare 403 is
  // still treated as a challenge signal.
  return detectLegacy
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface WithStepUpOptions extends StepUpRequest {
  /** Treat a bare 403 (no {@code stepup_required} body) as a step-up
   * challenge. Set this for the bank/integration routes that still use
   * the {@code X-Kori-Step-Up} header and return a plain 403. */
  detectLegacy?: boolean
}

export function useStepUp(): {
  requireStepUp: (request: StepUpRequest) => Promise<string>
  withStepUp: <T>(
    run: (headers: Record<string, string>) => Promise<T>,
    options: WithStepUpOptions,
  ) => Promise<T>
} {
  /**
   * Open the step-up modal and resolve with a fresh token bound to the given
   * action. Rejects if the user cancels.
   */
  function requireStepUp(request: StepUpRequest): Promise<string> {
    return openChallenge(request)
  }

  /**
   * Run {@code run} once; if the server rejects with a {@code stepup_required}
   * challenge, prompt the user, then retry once with the token injected on
   * the configured header. All other errors bubble up.
   */
  async function withStepUp<T>(
    run: (headers: Record<string, string>) => Promise<T>,
    options: WithStepUpOptions,
  ): Promise<T> {
    try {
      return await run({})
    } catch (err) {
      if (!isStepUpRequired(err, options.detectLegacy)) throw err
      const token = await requireStepUp(options)
      const header = options.header ?? 'X-Step-Up-Token'
      return await run({ [header]: token })
    }
  }

  return { requireStepUp, withStepUp }
}

// ---------------------------------------------------------------------------
// Internal API — consumed by StepUpModal.vue (NOT part of the public surface).
// Exported so the modal can subscribe without duplicating module state.
// ---------------------------------------------------------------------------

export const __stepUpInternal = {
  activeChallenge: readonly(activeChallenge),
  lastError: readonly(lastError),
  async submit(code: string): Promise<void> {
    const current = activeChallenge.value
    if (!current) return
    try {
      const { token } = await issuer({ action: current.action, code })
      current.resolve(token)
      nextChallenge()
    } catch (e) {
      const ae = e as Partial<ApiError> | null
      lastError.value = ae?.message ?? 'Unable to verify — check the code and try again.'
      // Keep the modal open so the user can retry with a fresh code.
    }
  },
  cancel(): void {
    const current = activeChallenge.value
    if (!current) return
    current.reject(new StepUpCancelled(current.action))
    nextChallenge()
  },
}

/** Thrown through {@code requireStepUp} / {@code withStepUp} when the user
 * dismisses the modal instead of completing the challenge. Callers can
 * catch this explicitly to distinguish "user declined" from real failures. */
export class StepUpCancelled extends Error {
  readonly action: string
  constructor(action: string) {
    super(`Step-up cancelled by user for action: ${action}`)
    this.name = 'StepUpCancelled'
    this.action = action
  }
}
